import React, { useState, useEffect } from 'react';
import {
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Box,
  Collapse,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Chip,
  TextField,
  Popover,
  CircularProgress,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; 
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LabelIcon from '@mui/icons-material/Label';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SecurityIcon from '@mui/icons-material/Security';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import GroupsIcon from '@mui/icons-material/Groups';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import HubIcon from '@mui/icons-material/Hub';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import MilitaryTech from '@mui/icons-material/MilitaryTech';
import Groups2Icon from '@mui/icons-material/Groups2';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';


/* NOUVELLE ICÔNE POUR L'AGENDA */
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';


import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { useTranslation } from 'react-i18next';
import { useClubTheme } from '../contexts/ClubThemeContext';
import DisclaimerDialog from './DisclaimerDialog';

/* NOUVELLE IMPORTATION D'ICÔNE POUR AGENDAS
   (Déjà fait ci-dessus : CalendarMonthIcon) */

/**
 * Récupération des groupes depuis l'API.
 * On lit la section via process.env.REACT_APP_SECTION.
 */
const fetchGroups = async (userId) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const token = localStorage.getItem('token');
  const clubId = localStorage.getItem('clubId'); // Multi-tenant: récupérer le club_id

  let url = `${backendUrl}/api/groups?section=${section}`;
  if (userId) {
    url += `&userId=${userId}`;
  }

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Club-Id': clubId || ''
    }
  });
  const data = await response.json();
  return data; // renvoie un tableau d'objets { id, libelle, code_couleur, cantera: bool }
};

/**
 * Récupération des matchs depuis l'API pour une date donnée
 * @param {string} date - Date au format YYYY-MM-DD
 */
const fetchScheduleForDate = async (date) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const section = process.env.REACT_APP_SECTION || 'hommes';
    const token = localStorage.getItem('token');
    const clubId = localStorage.getItem('clubId'); // Multi-tenant: récupérer le club_id

    const response = await fetch(`${backendUrl}/api/schedule/dashboard?date=${date}&section=${section}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Club-Id': clubId || '' // Ajouter le header X-Club-Id
      }
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des matchs');
    }

    const data = await response.json();
    return data || { matches: [], totalPlayers: 0 };
  } catch (error) {
    console.error('Erreur lors de la récupération des matchs:', error);
    return { matches: [], totalPlayers: 0 };
  }
};

function Layout({ onLogout }) {
  const { t, i18n } = useTranslation();
  const { primaryColor, secondaryColor, logo, clubName } = useClubTheme();

  // Détecter le club via l'URL pour afficher le logo correspondant
  const currentUrl = window.location.href.toLowerCase();
  const isKansas = currentUrl.includes('kansas');
  const isMetropolitans = currentUrl.includes('metropolitans');
  const isByers = currentUrl.includes('byers');
  const layoutLogoPath = isKansas ? '/assets/kansas.png'
    : isMetropolitans ? '/assets/metropolitans.png'
    : isByers ? '/assets/byers.png'
    : '/assets/valenciaapp.png';

  const [user, setUser] = useState(null);

  // États pour les ouvertures/fermetures de menus
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const [canteraMenuOpen, setCanteraMenuOpen] = useState(false); // Sous-menu "Cantera"

  const [activeMenuItem, setActiveMenuItem] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [cameFromDashboard, setCameFromDashboard] = useState(false);

  // État pour stocker les groupes Cantera
  const [canteraGroups, setCanteraGroups] = useState([]);

  // États pour le sélecteur de matchs
  // Initialiser avec la date du jour au format YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [totalPlayers, setTotalPlayers] = useState(0);

  // États pour le popup des joueurs
  const [matchPopoverAnchor, setMatchPopoverAnchor] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchPlayers, setMatchPlayers] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // États pour le popup de la liste des matchs
  const [matchesListAnchor, setMatchesListAnchor] = useState(null);

  // État pour le scouting report
  const [loadingScoutingReport, setLoadingScoutingReport] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  // Au montage, on charge l'utilisateur et on récupère les groupes Cantera
  useEffect(() => {
    setMenuOpen(isLargeScreen);
    // Sauvegarder l'état initial
    localStorage.setItem('menuOpen', isLargeScreen.toString());

    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      setUser(userData);
    } else {
      navigate('/login');
    }
  }, [navigate, isLargeScreen]);

  // Détecter si on vient du Dashboard
  useEffect(() => {
    if (location.pathname === '/informes') {
      setActiveMenuItem('Informes');
      if (location.state?.activeTab !== undefined) {
        setCameFromDashboard(true);
      }
    } else {
      setCameFromDashboard(false);
    }
  }, [location]);

  // Charger la liste des groupes "cantera" depuis l'API
  useEffect(() => {
    const loadGroups = async () => {
      try {
        // Pour les entraîneurs, filtrer par userId pour ne récupérer que leurs groupes
        const userId = user?.role === 'entrenador' ? user.id : null;
        const allGroups = await fetchGroups(userId);
        const onlyCantera = allGroups.filter((g) => g.cantera === true);
        setCanteraGroups(onlyCantera);
      } catch (error) {
        console.error('Erreur lors du chargement des groupes:', error);
      }
    };

    if (user) {
      loadGroups();
    }
  }, [user]);

  // Charger les matchs pour la date sélectionnée
  useEffect(() => {
    const loadMatches = async () => {
      setLoadingMatches(true);
      try {
        const data = await fetchScheduleForDate(selectedDate);
        setMatches(data.matches || []);
        setTotalPlayers(data.totalPlayers || 0);
      } catch (error) {
        console.error('Erreur lors du chargement des matchs:', error);
        setMatches([]);
        setTotalPlayers(0);
      } finally {
        setLoadingMatches(false);
      }
    };

    // Charger les matchs seulement si l'utilisateur est connecté
    if (user) {
      loadMatches();
    }
  }, [selectedDate]);

  // Charger les matchs au montage initial quand user devient disponible
  useEffect(() => {
    if (user && matches.length === 0 && !loadingMatches) {
      const loadInitialMatches = async () => {
        setLoadingMatches(true);
        try {
          const data = await fetchScheduleForDate(selectedDate);
          setMatches(data.matches || []);
          setTotalPlayers(data.totalPlayers || 0);
        } catch (error) {
          console.error('Erreur lors du chargement initial des matchs:', error);
        } finally {
          setLoadingMatches(false);
        }
      };
      loadInitialMatches();
    }
  }, [user]);

  const toggleMenu = () => {
    const newMenuState = !menuOpen;
    setMenuOpen(newMenuState);
    // Sauvegarder l'état dans localStorage pour que les autres composants puissent le lire
    localStorage.setItem('menuOpen', newMenuState.toString());
    // Déclencher un événement pour notifier les autres composants
    window.dispatchEvent(new Event('storage'));
  };

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
  };

  const toggleCrear = () => {
    setCrearOpen(!crearOpen);
  };

  const toggleCanteraMenu = () => {
    setCanteraMenuOpen(!canteraMenuOpen);
  };

  /**
   * Renvoie la liste des items de menu en fonction du rôle de l'utilisateur.
   */
  const getMenuItems = () => {
    if (!user) return [];

    switch (user.role) {
      case 'super admin':
        return [
          t('layout.menu.main'),
          t('layout.menu.players'),
          t('layout.menu.bestOfDay'),
          t('layout.menu.competitions'),
          t('layout.menu.cantera'),
          t('layout.menu.reports'),
          t('layout.menu.lists'),
          t('layout.menu.agencies'),
          t('layout.menu.create'),
          t('layout.menu.settings'),
        ];
      case 'admin pro':
        return [
          t('layout.menu.main'),
          t('layout.menu.players'),
          t('layout.menu.bestOfDay'),
          t('layout.menu.competitions'),
          t('layout.menu.reports'),
          t('layout.menu.lists'),
          t('layout.menu.agencies'),
          t('layout.menu.create'),
        ];
      case 'admin cantera':
        return [
          t('layout.menu.main'),
          t('layout.menu.players'),
          t('layout.menu.bestOfDay'),
          t('layout.menu.cantera'),
          t('layout.menu.reports'),
          t('layout.menu.lists'),
          t('layout.menu.agencies'),
          t('layout.menu.create'),
        ];
      case 'entrenador':
        return [
          t('layout.menu.players'),
          t('layout.menu.cantera'),
          t('layout.menu.reports'),
          t('layout.menu.intel'),
          t('layout.menu.rankings'),
          t('layout.menu.tracking'),
          t('layout.menu.create'),
        ];
      case 'PDP':
        return [
          t('layout.menu.reports'),
          t('layout.menu.intel'),
          t('layout.menu.rankings'),
          t('layout.menu.tracking'),
          t('layout.menu.create'),
        ];
      case 'scout interne':
      case 'scout externe':
      case 'scout interne cantera':
      case 'scout externe cantera':
        return [
          t('layout.menu.players'),
          t('layout.menu.reports'),
          t('layout.menu.intel'),
          t('layout.menu.rankings'),
          t('layout.menu.tracking'),
          t('layout.menu.create'),
        ];
      default:
        return [];
    }
  };

  /**
   * Renvoie la liste des items pour la configuration (paramètres) selon le rôle.
   */
  const getSettingsMenuItems = () => {
    if (user?.role === 'super admin') {
      return [
        { name: t('layout.settingsMenu.users'), icon: GroupIcon, path: 'usuarios' },
        { name: t('layout.settingsMenu.groups'), icon: HubIcon, path: 'grupos' },
        { name: t('layout.settingsMenu.tags'), icon: LabelIcon, path: 'tags' },
        // NOUVEL ITEM "Tags Agenda" (super admin)
        { name: t('layout.settingsMenu.agendaTags'), icon: LabelIcon, path: 'agenda-tags' },
        { name: t('layout.settingsMenu.statisticsTags'), icon: LabelIcon, path: 'cantera-tags' },
        { name: t('layout.settingsMenu.instatTags'), icon: LabelIcon, path: 'instat-tags' },
        { name: t('layout.settingsMenu.range'), icon: StarIcon , path: 'range' },
        { name: t('layout.settingsMenu.competitions'), icon: MilitaryTech, path: 'competitions-management' },

        // { name: t('layout.settingsMenu.teamAssignment'), icon: SportsSoccerIcon, path: 'players-without-team' },
        { name: t('layout.settingsMenu.import'), icon: FileUploadIcon, path: 'importar' },
        { name: t('layout.settingsMenu.instat'), icon: SportsBasketballIcon, path: 'instat-poc' },
      ];
    } else {
      return [
        { name: t('layout.settingsMenu.groups'), icon: HubIcon, path: 'grupos' },
        { name: t('layout.settingsMenu.tags'), icon: LabelIcon, path: 'tags' },
        { name: t('layout.settingsMenu.import'), icon: FileUploadIcon, path: 'importar' },
      ];
    }
  };

  /**
   * Helper function to navigate with confirmation if game in progress
   */
  const navigateWithConfirmation = (path) => {
    if (window.estadisticasConfirmNavigation) {
      const canNavigate = window.estadisticasConfirmNavigation(() => {
        navigate(path);
      });
      if (canNavigate) {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  };

  /**
   * Gère la logique de navigation + mise à jour de l'élément actif lors du clic.
   */
  const handleMenuItemClick = (item, type) => {
    setActiveMenuItem(type === 'create' ? `crear:${item}` : item);

    // Menu principal
    if (item === t('layout.menu.main')) {
      navigateWithConfirmation('/menu');
    } else if (item === t('layout.menu.players')) {
      navigateWithConfirmation('/jugadores');
    } else if (item === t('layout.menu.projects')) {
      navigateWithConfirmation('/tecnification');
    } else if (item === t('layout.menu.reports')) {
      // Pour admin pro, admin cantera et super admin, navigue vers /informes
      if (user?.role === 'super admin' || user?.role === 'admin pro' || user?.role === 'admin cantera') {
        navigateWithConfirmation('/informes');
      } else {
        navigateWithConfirmation('/buscar-informe');
      }
    } else if (item === t('layout.menu.intel')) {
      navigateWithConfirmation('/buscar-intel');
    } else if (item === t('layout.menu.rankings')) {
      if ((type === 'search' || user?.role === 'entrenador' || user?.role === 'scout interne' || user?.role === 'scout externe' || user?.role === 'scout interne cantera' || user?.role === 'scout externe cantera') && type !== 'create') {
        navigateWithConfirmation('/buscar-bigboard');
      } else {
        navigateWithConfirmation('/big-board');
      }
    } else if (item === t('layout.menu.lists')) {
      // Pour admin pro, admin cantera et super admin
      navigateWithConfirmation('/scouting');
    } else if (item === t('layout.menu.competitions')) {
      // Si c'est depuis le menu de configuration, aller vers management
      if (type === 'config') {
        navigateWithConfirmation('/competitions-management');
      } else {
        // Sinon, aller vers la vue
        navigateWithConfirmation('/competitions-vue');
      }
    } else if (item === t('layout.menu.tracking')) {
      if ((user?.role === 'entrenador' || user?.role === 'scout interne' || user?.role === 'scout externe' || user?.role === 'scout interne cantera' || user?.role === 'scout externe cantera') && type !== 'create') {
        navigateWithConfirmation('/buscar-seguimiento');
      } else {
        navigateWithConfirmation('/seguimiento');
      }
    } else if (item === t('layout.menu.news')) {
      navigateWithConfirmation('/news');
    } else if (item === t('layout.menu.bestOfDay')) {
      navigateWithConfirmation('/resume');
    } else if (item === t('layout.menu.settings')) {
      // Géré par toggleSettings(), pas de navigation directe
    } else if (item === t('layout.settingsMenu.users')) {
      navigateWithConfirmation('/usuarios');
    } else if (item === t('layout.settingsMenu.groups')) {
      navigateWithConfirmation('/grupos');
    } else if (item === t('layout.settingsMenu.tags')) {
      navigateWithConfirmation('/tags');
    } else if (item === t('layout.settingsMenu.range')) {
      navigateWithConfirmation('/range');
    } else if (item === t('layout.settingsMenu.import')) {
      navigateWithConfirmation('/importar');
    } else if (item === t('layout.settingsMenu.agendaTags')) {
      navigateWithConfirmation('/agenda-tags');
    } else if (item === t('layout.settingsMenu.statisticsTags')) {
      navigateWithConfirmation('/cantera-tags');
    } else if (item === t('layout.settingsMenu.instatTags')) {
      navigateWithConfirmation('/instat-tags');
    } else if (item === t('layout.settingsMenu.teamAssignment')) {
      navigateWithConfirmation('/players-without-team');
    } else if (item === t('layout.settingsMenu.instat')) {
      navigateWithConfirmation('/instat-poc');
    } else if (item === t('layout.menu.create')) {
      // Géré par toggleCrear(), pas de navigation directe
    } else if (item === t('layout.createMenu.player')) {
      navigateWithConfirmation('/crear-jugador');
    } else if (item === t('layout.createMenu.ficha')) {
      navigateWithConfirmation('/crear-ficha');
    } else if (item === t('layout.createMenu.rankings')) {
      navigateWithConfirmation('/big-board');
    } else if (item === t('layout.createMenu.tracking')) {
      navigateWithConfirmation('/seguimiento');
    } else if (item === t('layout.menu.agencies')) {
      navigateWithConfirmation('/agencia');
    } else if (item === t('layout.menu.agenda')) {
      navigateWithConfirmation('/agenda');
    } else {
      navigateWithConfirmation(`/${item.toLowerCase().replace(/\s+/g, '-')}`);
    }
  };

  /**
   * Navigation quand on clique sur un groupe cantera du sous-menu
   */
  const handleCanteraGroupClick = (groupId) => {
    // Par exemple : /cantera/:id
    navigateWithConfirmation(`/cantera/${groupId}`);
  };

  /**
   * Retourne l'URL de l'avatar de l'utilisateur
   */
  const getAvatarUrl = (userId) => {
    const url = userId
      ? `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`
      : '/path/to/default-avatar.png';
    return url;
  };

  const handleAvatarClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    onLogout();
  };

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    // Les matchs seront chargés automatiquement via le useEffect
  };

  // Fonctions pour le popup des joueurs
  const handleMatchClick = async (event, match, index) => {
    setMatchPopoverAnchor(event.currentTarget);
    setSelectedMatch(match);
    setCurrentMatchIndex(index);
    setLoadingPlayers(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const section = process.env.REACT_APP_SECTION || 'hommes';
      const token = localStorage.getItem('token');
      const clubId = localStorage.getItem('clubId'); // Multi-tenant

      // Construire l'URL avec les logos et la date
      const params = new URLSearchParams({
        team1: match.team1,
        team2: match.team2,
        section: section,
        logo1: match.logo1 || '',
        logo2: match.logo2 || '',
        gameDate: selectedDate || ''
      });

      const response = await fetch(
        `${backendUrl}/api/schedule/match-players?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Club-Id': clubId || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMatchPlayers(data);
      } else {
        setMatchPlayers(null);
      }
    } catch (error) {
      console.error('Error fetching match players:', error);
      setMatchPlayers(null);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const handleClosePopover = () => {
    setMatchPopoverAnchor(null);
    setSelectedMatch(null);
    setMatchPlayers(null);
  };

  // Gestion du popup de la liste des matchs
  const handleMatchesListClick = (event) => {
    setMatchesListAnchor(event.currentTarget);
  };

  const handleCloseMatchesList = () => {
    setMatchesListAnchor(null);
  };

  // Générer un scouting report pour le match sélectionné
  const handleScoutingReport = async () => {
    if (!selectedMatch) return;

    // Ouvrir la fenêtre IMMÉDIATEMENT (geste utilisateur → pas bloqué par anti-popup)
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      alert(t('layout.scoutingReportError'));
      return;
    }

    // Traductions du loader
    const loaderTr = {
      en: { generating: 'Generating scouting report...', duration: 'This usually takes about a minute', step1: 'Searching match...', step2: 'Scraping team data & rosters...', step3: 'Fetching player statistics...', step4: 'Building HTML report...' },
      es: { generating: 'Generando informe de scouting...', duration: 'Esto suele tardar alrededor de un minuto', step1: 'Buscando partido...', step2: 'Recopilando datos de equipos...', step3: 'Obteniendo estadísticas de jugadores...', step4: 'Generando informe HTML...' },
      fr: { generating: 'Génération du rapport de scouting...', duration: 'Cela prend environ une minute', step1: 'Recherche du match...', step2: 'Récupération des données d\'équipes...', step3: 'Récupération des statistiques joueurs...', step4: 'Construction du rapport HTML...' }
    };
    const lt = loaderTr[i18n.language] || loaderTr.en;

    // Afficher un loader dans la nouvelle fenêtre
    reportWindow.document.write(`<!DOCTYPE html>
<html lang="${i18n.language || 'en'}"><head><meta charset="UTF-8">
<title>${selectedMatch.team1} vs ${selectedMatch.team2} - Scouting Report</title>
<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;600&family=Barlow:wght@400;500&display=swap" rel="stylesheet">
<style>
  body { background: #090B0F; color: #EEF0F4; font-family: 'Barlow', sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; overflow: hidden; }

  .ball-container { position: relative; width: 80px; height: 120px; }
  .ball {
    width: 48px; height: 48px; position: absolute; left: 16px;
    animation: bounce 0.6s ease-in-out infinite;
  }
  .ball svg { width: 48px; height: 48px; }
  .ball-shadow {
    width: 40px; height: 8px; border-radius: 50%;
    background: rgba(249,115,22,0.3);
    position: absolute; bottom: 0; left: 20px;
    animation: shadow 0.6s ease-in-out infinite;
  }
  @keyframes bounce {
    0%, 100% { bottom: 0; animation-timing-function: ease-out; }
    50% { bottom: 60px; animation-timing-function: ease-in; }
  }
  @keyframes shadow {
    0%, 100% { transform: scaleX(1); opacity: 0.3; }
    50% { transform: scaleX(0.6); opacity: 0.15; }
  }

  h2 { font-family: 'Oswald', sans-serif; margin-top: 1.5rem; font-weight: 600; letter-spacing: 0.04em; font-size: 1.3rem; }
  .subtitle { color: #8891A5; font-size: 0.9rem; margin-top: 0.5rem; }
  .duration { color: #505868; font-size: 0.78rem; margin-top: 0.3rem; font-style: italic; }
  .steps { margin-top: 2rem; text-align: left; color: #505868; font-size: 0.8rem; line-height: 2.2; }
  .steps div { display: flex; align-items: center; gap: 0.5rem; }
  .steps div { transition: color 0.3s; }
  .steps .done { color: #2DD4A0; }
  .steps .active { color: #F97316; }
  .step-dot { width: 6px; height: 6px; border-radius: 50%; background: #505868; flex-shrink: 0; transition: background 0.3s; }
  .steps .active .step-dot { background: #F97316; animation: pulse 1s ease-in-out infinite; }
  .steps .done .step-dot { background: #2DD4A0; animation: none; }
  .step-check { display: none; color: #2DD4A0; font-size: 0.7rem; }
  .steps .done .step-check { display: inline; }
  .steps .done .step-dot { display: none; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

  .progress-bar { width: 200px; height: 3px; background: #1A1F2C; border-radius: 2px; margin-top: 2rem; overflow: hidden; }
  .progress-fill { height: 100%; background: #F97316; border-radius: 2px; animation: progress 60s linear forwards; }
  @keyframes progress { from { width: 0%; } to { width: 100%; } }
</style>
</head><body>
  <div class="ball-container">
    <div class="ball">
      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="22" fill="#F97316" stroke="#E8650F" stroke-width="1.5"/>
        <path d="M24 2C24 2 24 46 24 46" stroke="#1A1F2C" stroke-width="1.2" opacity="0.5"/>
        <path d="M2 24C2 24 46 24 46 24" stroke="#1A1F2C" stroke-width="1.2" opacity="0.5"/>
        <path d="M7 7C14 14 14 34 7 41" stroke="#1A1F2C" stroke-width="1.2" opacity="0.5" fill="none"/>
        <path d="M41 7C34 14 34 34 41 41" stroke="#1A1F2C" stroke-width="1.2" opacity="0.5" fill="none"/>
      </svg>
    </div>
    <div class="ball-shadow"></div>
  </div>
  <h2>${selectedMatch.team1} vs ${selectedMatch.team2}</h2>
  <p class="subtitle">${lt.generating}</p>
  <p class="duration">${lt.duration}</p>
  <div class="steps" id="steps">
    <div class="active"><span class="step-dot"></span><span class="step-check">&#10003;</span>${lt.step1}</div>
    <div><span class="step-dot"></span><span class="step-check">&#10003;</span>${lt.step2}</div>
    <div><span class="step-dot"></span><span class="step-check">&#10003;</span>${lt.step3}</div>
    <div><span class="step-dot"></span><span class="step-check">&#10003;</span>${lt.step4}</div>
  </div>
  <div class="progress-bar"><div class="progress-fill"></div></div>
  <script>
    (function() {
      var steps = document.querySelectorAll('.steps > div');
      var current = 0;
      var timings = [8000, 15000, 25000]; // ms avant de passer au step suivant
      function advance() {
        if (current >= steps.length - 1) return;
        steps[current].classList.remove('active');
        steps[current].classList.add('done');
        current++;
        steps[current].classList.add('active');
        if (current < timings.length) setTimeout(advance, timings[current]);
      }
      setTimeout(advance, timings[0]);
    })();
  </script>
</body></html>`);

    setLoadingScoutingReport(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        team1: selectedMatch.team1,
        team2: selectedMatch.team2,
        date: selectedDate,
        lang: i18n.language || 'en',
        clubSlug: localStorage.getItem('clubSlug') || '',
        mediaUrl: process.env.REACT_APP_MEDIA_URL || ''
      });
      const response = await fetch(
        `${backendUrl}/api/schedule/scouting-report?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Club-Id': localStorage.getItem('clubId') || ''
          }
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur lors de la génération du rapport');
      }
      const html = await response.text();
      // Remplacer le contenu du loader par le rapport
      reportWindow.document.open();
      reportWindow.document.write(html);
      reportWindow.document.close();
    } catch (error) {
      console.error('Erreur scouting report:', error);
      // Afficher l'erreur dans la fenêtre déjà ouverte
      if (reportWindow && !reportWindow.closed) {
        reportWindow.document.open();
        reportWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Error</title>
<style>body { background: #090B0F; color: #F0465A; font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; }
h2 { margin-bottom: 1rem; } p { color: #8891A5; }</style></head>
<body><h2>Error</h2><p>${error.message || 'Unknown error'}</p></body></html>`);
        reportWindow.document.close();
      }
    } finally {
      setLoadingScoutingReport(false);
    }
  };

  const navigateMatch = async (direction) => {
    const newIndex = direction === 'next'
      ? Math.min(currentMatchIndex + 1, matches.length - 1)
      : Math.max(currentMatchIndex - 1, 0);

    if (newIndex !== currentMatchIndex && matches[newIndex]) {
      setCurrentMatchIndex(newIndex);
      const newMatch = matches[newIndex];
      setSelectedMatch(newMatch);
      setLoadingPlayers(true);

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const section = process.env.REACT_APP_SECTION || 'hommes';
        const token = localStorage.getItem('token');
        const clubId = localStorage.getItem('clubId'); // Multi-tenant

        // Construire l'URL avec les logos et la date
        const params = new URLSearchParams({
          team1: newMatch.team1,
          team2: newMatch.team2,
          section: section,
          logo1: newMatch.logo1 || '',
          logo2: newMatch.logo2 || '',
          gameDate: selectedDate || ''
        });

        const response = await fetch(
          `${backendUrl}/api/schedule/match-players?${params.toString()}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Club-Id': clubId || ''
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setMatchPlayers(data);
        } else {
          setMatchPlayers(null);
        }
      } catch (error) {
        console.error('Error fetching match players:', error);
        setMatchPlayers(null);
      } finally {
        setLoadingPlayers(false);
      }
    }
  };

  // Fonction pour obtenir l'URL de l'avatar d'un joueur
  const getPlayerAvatarUrl = (playerId) => {
    return `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`;
  };

  /**
   * Gère le clic sur le logo Valencia App.
   * Redirige l'utilisateur vers la page d'accueil en fonction de son rôle.
   */
  const handleLogoClick = () => {
    if (!user) return;
    if (user.role === 'entrenador' || user.role === 'scout interne' || user.role === 'scout externe' || user.role === 'scout interne cantera' || user.role === 'scout externe cantera') {
      navigateWithConfirmation('/jugadores');
    } else if (user.role === 'PDP') {
      navigateWithConfirmation('/tecnification');
    } else {
      navigateWithConfirmation('/menu');
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <DisclaimerDialog />
      {/* APP BAR */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1201,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
        elevation={2}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '70px !important',
          }}
        >
          {/* MENU BURGER + LOGO */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleMenu}
              aria-label="menu"
              sx={{
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={layoutLogoPath}
                alt="Club Logo"
                style={{
                  height: '60px',
                  width: 'auto',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
                onClick={handleLogoClick}
              />
            </Box>
          </Box>

          {/* SÉLECTEUR DE DATE ET MATCHS */}
          {user?.role !== 'entrenador' && user?.role !== 'scout interne' && user?.role !== 'scout externe' && user?.role !== 'scout interne cantera' && user?.role !== 'scout externe cantera' && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, md: 0.75, xl: 1 },
            flex: 1,
            px: { xs: 1, md: 1.5, xl: 2 },
            overflow: 'hidden'
          }}>
            {/* Sélecteur de date */}
            <TextField
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                }
              }}
              sx={{
                minWidth: { xs: 120, md: 130, xl: 135 },
                maxWidth: { xs: 120, md: 130, xl: 135 },
                flexShrink: 0,
                '& .MuiInputBase-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  height: '28px',
                },
                '& .MuiInputBase-input': {
                  padding: '4px 8px',
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  fontWeight: '500',
                  colorScheme: 'dark',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(255,255,255,0.3)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                  fontSize: '18px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(255,255,255,0.5)',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(255,255,255,0.7)',
                }
              }}
            />

            {/* Chip avec le nombre de matchs */}
            {!loadingMatches && matches.length > 0 && (
              <Chip
                label={`${matches.length}`}
                onClick={handleMatchesListClick}
                sx={{
                  backgroundColor: matches.length > 5 ? '#4CAF50' :
                                   matches.length > 2 ? '#FF9800' : '#2196F3',
                  color: 'white',
                  height: '24px',
                  minWidth: '24px',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.65rem', md: '0.7rem' },
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  },
                  '& .MuiChip-label': {
                    px: 0.75,
                  }
                }}
              />
            )}

            {/* Flèche gauche pour faire défiler les matchs */}
            <IconButton
              size="small"
              onClick={() => {
                const matchContainer = document.querySelector('.matches-scroll-container');
                if (matchContainer) {
                  matchContainer.scrollBy({ left: -400, behavior: 'smooth' });
                }
              }}
              disabled={!matches.length}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                minWidth: '28px',
                width: '28px',
                height: '28px',
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '18px' }} />
            </IconButton>

            {/* Matchs */}
            <Box
              className="matches-scroll-container"
              sx={{
              display: 'flex',
              gap: { xs: 0.3, md: 0.4, xl: 0.5 },
              alignItems: 'center',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              flex: 1,
              minWidth: 0
            }}>
              {loadingMatches ? (
                <Typography sx={{ color: 'white', fontSize: '0.75rem', px: 1 }}>
                  {t('layout.loading')}
                </Typography>
              ) : matches.length === 0 ? (
                <Typography sx={{ color: 'white', fontSize: '0.75rem', px: 1 }}>
                  {t('layout.noMatchesToday')}
                </Typography>
              ) : (
                matches.map((match, index) => (
                  <Chip
                    key={index}
                    className={`match-chip-${index}`}
                    onClick={(event) => handleMatchClick(event, match, index)}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, md: 0.4 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
                            {/* Logo équipe 1 - URL ou emoji */}
                            {match.logo1 && match.logo1.startsWith('http') ? (
                              <img src={match.logo1} alt={match.team1} style={{ width: '12px', height: '12px', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '10px', lineHeight: 1 }}>{match.logo1}</span>
                            )}
                            <Typography variant="caption" sx={{
                              fontSize: { xs: '0.55rem', md: '0.6rem', xl: '0.65rem' },
                              fontWeight: 600,
                              lineHeight: 1,
                              maxWidth: { xs: '70px', md: '85px', xl: '100px' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {match.team1}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
                            {/* Logo équipe 2 - URL ou emoji */}
                            {match.logo2 && match.logo2.startsWith('http') ? (
                              <img src={match.logo2} alt={match.team2} style={{ width: '12px', height: '12px', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '10px', lineHeight: 1 }}>{match.logo2}</span>
                            )}
                            <Typography variant="caption" sx={{
                              fontSize: { xs: '0.55rem', md: '0.6rem', xl: '0.65rem' },
                              fontWeight: 600,
                              lineHeight: 1,
                              maxWidth: { xs: '70px', md: '85px', xl: '100px' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {match.team2}
                            </Typography>
                          </Box>
                        </Box>
                        {match.chip > 0 && (
                          <Box sx={{
                            backgroundColor: 'rgba(255,255,255,0.35)',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.55rem',
                            fontWeight: 'bold',
                            color: 'white',
                            flexShrink: 0
                          }}>
                            {match.chip}
                          </Box>
                        )}
                      </Box>
                    }
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      height: { xs: '36px', md: '38px', xl: '40px' },
                      minWidth: { xs: '100px', md: '120px', xl: '140px' },
                      fontSize: '0.7rem',
                      border: '1px solid rgba(255,255,255,0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        transform: 'scale(1.05)',
                        zIndex: 1,
                      },
                      '& .MuiChip-label': {
                        padding: { xs: '2px 4px', md: '3px 6px', xl: '4px 8px' },
                        display: 'flex',
                        overflow: 'visible'
                      }
                    }}
                  />
                ))
              )}
            </Box>

            {/* Flèche droite pour faire défiler les matchs */}
            <IconButton
              size="small"
              onClick={() => {
                const matchContainer = document.querySelector('.matches-scroll-container');
                if (matchContainer) {
                  matchContainer.scrollBy({ left: 400, behavior: 'smooth' });
                }
              }}
              disabled={!matches.length}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                minWidth: '28px',
                width: '28px',
                height: '28px',
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
          )}

          {/* AVATAR UTILISATEUR */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={handleAvatarClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '25px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  transform: 'translateX(-5px)',
                },
              }}
            >
              <span
                style={{
                  paddingRight: '10px',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                }}
              >
                {user ? `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}` : ''}
              </span>
              <Avatar
                src={getAvatarUrl(user?.id)}
                alt="Avatar"
                sx={{
                  marginRight: '10px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'border-color 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.6)',
                  },
                }}
              />
            </Box>
          </Box>
          <Menu
            id="avatar-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: '8px',
                minWidth: '150px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                backgroundColor: '#fff',
              },
              '& .MuiMenuItem-root': {
                padding: '10px 20px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#FFF3E0',
                  color: primaryColor,
                },
              },
            }}
          >
            <MenuItem onClick={handleLogoutClick}>{t('layout.logout')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* DRAWER GAUCHE */}
      <Drawer
        variant={isLargeScreen ? 'persistent' : 'temporary'}
        anchor="left"
        open={menuOpen}
        onClose={toggleMenu}
        sx={{
          width: 25,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 200,
            boxSizing: 'border-box',
            background: `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            color: '#ffffff',
            top: 70,
            height: 'calc(100% - 70px)',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
            borderRight: 'none',
          },
        }}
      >
        <Toolbar />
        <List
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            padding: '8px 4px',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.25)',
              borderRadius: '3px',
              transition: 'background 0.3s ease',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          {getMenuItems().map((item, index) => {
            // Sous-menu CONFIGURATION
            if (item === t('layout.menu.settings')) {
              return (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => {
                      setActiveMenuItem(item);
                      toggleSettings();
                    }}
                    onMouseEnter={() => setHoveredItem(item)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                      margin: '3px 6px',
                      borderRadius: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor:
                        activeMenuItem === item ? 'rgba(255, 255, 255, 0.15)' :
                        hoveredItem === item ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      transform: hoveredItem === item ? 'translateX(5px)' : 'translateX(0)',
                      '&:hover': {
                        color: '#FFCC80',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <SettingsIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                    <ListItemText primary={item} />
                    {settingsOpen ? (
                      <ExpandLessIcon sx={{ color: '#ffffff' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                    )}
                  </ListItem>
                  <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {getSettingsMenuItems().map((menuItem, idx) => (
                        <ListItem
                          button
                          key={idx}
                          onMouseEnter={() => setHoveredItem(menuItem.name)}
                          onMouseLeave={() => setHoveredItem(null)}
                          sx={{
                            pl: 4,
                            pr: 1,
                            py: 0.75,
                            margin: '2px 6px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color:
                              activeMenuItem === menuItem.name
                                ? '#FFCC80'
                                : '#ffffff',
                            backgroundColor:
                              activeMenuItem === menuItem.name ? 'rgba(255, 255, 255, 0.1)' :
                              hoveredItem === menuItem.name ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                            transition: 'all 0.2s ease',
                            transform: hoveredItem === menuItem.name ? 'translateX(3px)' : 'translateX(0)',
                            '&:hover': { color: '#FFCC80' },
                          }}
                          onClick={() => handleMenuItemClick(menuItem.name, 'config')}
                        >
                          <menuItem.icon
                            sx={{
                              color:
                                activeMenuItem === menuItem.name
                                  ? '#FFCC80'
                                  : '#ffffff',
                              mr: 2,
                            }}
                          />
                          <ListItemText
                            primary={menuItem.name}
                            sx={{
                              color:
                                activeMenuItem === menuItem.name
                                  ? '#FFCC80'
                                  : '#ffffff',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Sous-menu "Crear"
            if (item === t('layout.menu.create')) {
              return (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => {
                      setActiveMenuItem(item);
                      toggleCrear();
                    }}
                    onMouseEnter={() => setHoveredItem(item)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                      margin: '3px 6px',
                      borderRadius: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor:
                        activeMenuItem === item ? 'rgba(255, 255, 255, 0.15)' :
                        hoveredItem === item ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      transform: hoveredItem === item ? 'translateX(5px)' : 'translateX(0)',
                      '&:hover': {
                        color: '#FFCC80',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <AddCircleIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                    <ListItemText primary={item} />
                    {crearOpen ? (
                      <ExpandLessIcon sx={{ color: '#ffffff' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                    )}
                  </ListItem>
                  <Collapse in={crearOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem
                        button
                        onMouseEnter={() => setHoveredItem(t('layout.createMenu.player'))}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          pl: 4,
                          pr: 1,
                          py: 0.75,
                          margin: '2px 6px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color:
                            activeMenuItem === `crear:${t('layout.createMenu.player')}`
                              ? '#FFCC80'
                              : '#ffffff',
                          backgroundColor:
                            activeMenuItem === `crear:${t('layout.createMenu.player')}` ? 'rgba(255, 255, 255, 0.1)' :
                            hoveredItem === t('layout.createMenu.player') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                          transition: 'all 0.2s ease',
                          transform: hoveredItem === t('layout.createMenu.player') ? 'translateX(3px)' : 'translateX(0)',
                          '&:hover': { color: '#FFCC80' },
                        }}
                        onClick={() => handleMenuItemClick(t('layout.createMenu.player'), 'create')}
                      >
                        <PersonIcon
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.player')}`
                                ? '#FFCC80'
                                : '#ffffff',
                            mr: 2,
                          }}
                        />
                        <ListItemText
                          primary={t('layout.createMenu.player')}
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.player')}`
                                ? '#FFCC80'
                                : '#ffffff',
                          }}
                        />
                      </ListItem>

                      <ListItem
                        button
                        onMouseEnter={() => setHoveredItem(t('layout.createMenu.ficha'))}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          pl: 4,
                          pr: 1,
                          py: 0.75,
                          margin: '2px 6px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color:
                            activeMenuItem === `crear:${t('layout.createMenu.ficha')}`
                              ? '#FFCC80'
                              : '#ffffff',
                          backgroundColor:
                            activeMenuItem === `crear:${t('layout.createMenu.ficha')}` ? 'rgba(255, 255, 255, 0.1)' :
                            hoveredItem === t('layout.createMenu.ficha') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                          transition: 'all 0.2s ease',
                          transform: hoveredItem === t('layout.createMenu.ficha') ? 'translateX(3px)' : 'translateX(0)',
                          '&:hover': { color: '#FFCC80' },
                        }}
                        onClick={() => handleMenuItemClick(t('layout.createMenu.ficha'), 'create')}
                      >
                        <AssignmentIcon
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.ficha')}`
                                ? '#FFCC80'
                                : '#ffffff',
                            mr: 2,
                          }}
                        />
                        <ListItemText
                          primary={t('layout.createMenu.ficha')}
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.ficha')}`
                                ? '#FFCC80'
                                : '#ffffff',
                          }}
                        />
                      </ListItem>

                      <ListItem
                        button
                        onMouseEnter={() => setHoveredItem(t('layout.createMenu.rankings'))}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          pl: 4,
                          pr: 1,
                          py: 0.75,
                          margin: '2px 6px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color:
                            activeMenuItem === `crear:${t('layout.createMenu.rankings')}`
                              ? '#FFCC80'
                              : '#ffffff',
                          backgroundColor:
                            activeMenuItem === `crear:${t('layout.createMenu.rankings')}` ? 'rgba(255, 255, 255, 0.1)' :
                            hoveredItem === t('layout.createMenu.rankings') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                          transition: 'all 0.2s ease',
                          transform: hoveredItem === t('layout.createMenu.rankings') ? 'translateX(3px)' : 'translateX(0)',
                          '&:hover': { color: '#FFCC80' },
                        }}
                        onClick={() =>
                          handleMenuItemClick(t('layout.createMenu.rankings'), 'create')
                        }
                      >
                        <ViewKanbanIcon
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.rankings')}`
                                ? '#FFCC80'
                                : '#ffffff',
                            mr: 2,
                          }}
                        />
                        <ListItemText
                          primary={t('layout.createMenu.rankings')}
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.rankings')}`
                                ? '#FFCC80'
                                : '#ffffff',
                          }}
                        />
                      </ListItem>

                      <ListItem
                        button
                        onMouseEnter={() => setHoveredItem(t('layout.createMenu.tracking'))}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          pl: 4,
                          pr: 1,
                          py: 0.75,
                          margin: '2px 6px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color:
                            activeMenuItem === `crear:${t('layout.createMenu.tracking')}`
                              ? '#FFCC80'
                              : '#ffffff',
                          backgroundColor:
                            activeMenuItem === `crear:${t('layout.createMenu.tracking')}` ? 'rgba(255, 255, 255, 0.1)' :
                            hoveredItem === t('layout.createMenu.tracking') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                          transition: 'all 0.2s ease',
                          transform: hoveredItem === t('layout.createMenu.tracking') ? 'translateX(3px)' : 'translateX(0)',
                          '&:hover': { color: '#FFCC80' },
                        }}
                        onClick={() =>
                          handleMenuItemClick(t('layout.createMenu.tracking'), 'create')
                        }
                      >
                        <VisibilityIcon
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.tracking')}`
                                ? '#FFCC80'
                                : '#ffffff',
                            mr: 2,
                          }}
                        />
                        <ListItemText
                          primary={t('layout.createMenu.tracking')}
                          sx={{
                            color:
                              activeMenuItem === `crear:${t('layout.createMenu.tracking')}`
                                ? '#FFCC80'
                                : '#ffffff',
                          }}
                        />
                      </ListItem>
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Menu "Menu" (cas normal, sans Cantera intégré)
            if (
              (user.role === 'super admin' || user.role === 'admin cantera') &&
              item === t('layout.menu.main')
            ) {
              return (
                <React.Fragment key={index}>
                  {/* Item "Menu" normal */}
                  <ListItem
                    button
                    onClick={() => handleMenuItemClick(item)}
                    onMouseEnter={() => setHoveredItem(item)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                      margin: '3px 6px',
                      borderRadius: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor:
                        activeMenuItem === item ? 'rgba(255, 255, 255, 0.15)' :
                        hoveredItem === item ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      transform: hoveredItem === item ? 'translateX(5px)' : 'translateX(0)',
                      '&:hover': {
                        color: '#FFCC80',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <DashboardIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                    <ListItemText primary={item} />
                  </ListItem>
                </React.Fragment>
              );
            }

            // Menu Cantera avec sous-menu
            if (item === t('layout.menu.cantera')) {
              return (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => {
                      setActiveMenuItem(t('layout.menu.cantera'));
                      toggleCanteraMenu();
                    }}
                    onMouseEnter={() => setHoveredItem(t('layout.menu.cantera'))}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      color: activeMenuItem === t('layout.menu.cantera') ? '#FFCC80' : '#ffffff',
                      margin: '4px 12px',
                      borderRadius: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor:
                        activeMenuItem === t('layout.menu.cantera') ? 'rgba(255, 255, 255, 0.15)' :
                        hoveredItem === t('layout.menu.cantera') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      transform: hoveredItem === t('layout.menu.cantera') ? 'translateX(5px)' : 'translateX(0)',
                      '&:hover': {
                        color: '#FFCC80',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <GroupWorkIcon
                      sx={{
                        color:
                          activeMenuItem === t('layout.menu.cantera') ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                    <ListItemText primary={t('layout.menu.cantera')} />
                    {canteraMenuOpen ? (
                      <ExpandLessIcon sx={{ color: '#ffffff' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                    )}
                  </ListItem>
                  <Collapse in={canteraMenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {user?.role !== 'entrenador' && (
                      <ListItem
                        button
                        onMouseEnter={() => setHoveredItem(t('layout.menu.projects'))}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          pl: 4,
                          pr: 1,
                          py: 0.75,
                          margin: '2px 6px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: activeMenuItem === t('layout.menu.projects') ? '#FFCC80' : '#ffffff',
                          backgroundColor:
                            activeMenuItem === t('layout.menu.projects') ? 'rgba(255, 255, 255, 0.1)' :
                            hoveredItem === t('layout.menu.projects') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                          transition: 'all 0.2s ease',
                          transform: hoveredItem === t('layout.menu.projects') ? 'translateX(3px)' : 'translateX(0)',
                          '&:hover': { color: '#FFCC80' },
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={() => handleMenuItemClick(t('layout.menu.projects'))}
                      >
                        <SportsBasketballIcon
                          sx={{
                            color: activeMenuItem === t('layout.menu.projects') ? '#FFCC80' : '#ffffff',
                            mr: 2,
                            fontSize: 20,
                          }}
                        />
                        <ListItemText primary={t('layout.menu.projects')} />
                      </ListItem>
                      )}

                      {/* Agenda dans le sous-menu Cantera */}
                      {(user?.role === 'super admin' || user?.role === 'admin cantera' || user?.role === 'entrenador') && (
                        <ListItem
                          button
                          onMouseEnter={() => setHoveredItem(t('layout.menu.agenda'))}
                          onMouseLeave={() => setHoveredItem(null)}
                          sx={{
                            pl: 4,
                            pr: 1,
                            py: 0.75,
                            margin: '2px 6px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: activeMenuItem === t('layout.menu.agenda') ? '#FFCC80' : '#ffffff',
                            backgroundColor:
                              activeMenuItem === t('layout.menu.agenda') ? 'rgba(255, 255, 255, 0.1)' :
                              hoveredItem === t('layout.menu.agenda') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                            transition: 'all 0.2s ease',
                            transform: hoveredItem === t('layout.menu.agenda') ? 'translateX(3px)' : 'translateX(0)',
                            '&:hover': { color: '#FFCC80' },
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onClick={() => handleMenuItemClick(t('layout.menu.agenda'))}
                        >
                          <CalendarMonthIcon
                            sx={{
                              color: activeMenuItem === t('layout.menu.agenda') ? '#FFCC80' : '#ffffff',
                              mr: 2,
                              fontSize: 20,
                            }}
                          />
                          <ListItemText primary={t('layout.menu.agenda')} />
                        </ListItem>
                      )}

                      <Divider sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        my: 1,
                        mx: 2,
                        borderRadius: '1px',
                        height: '1px',
                      }} />
                      {canteraGroups.length === 0 && (
                        <ListItem sx={{ pl: 4 }}>
                          <ListItemText
                            primary={t('layout.noGroups')}
                            sx={{ color: '#ffffff' }}
                          />
                        </ListItem>
                      )}
                      {canteraGroups.map((group) => (
                        <ListItem
                          button
                          key={group.id}
                          onMouseEnter={() => setHoveredItem(`cantera-${group.id}`)}
                          onMouseLeave={() => setHoveredItem(null)}
                          sx={{
                            pl: 4,
                            pr: 1,
                            py: 0.75,
                            margin: '2px 6px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#ffffff',
                            backgroundColor:
                              hoveredItem === `cantera-${group.id}` ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                            transition: 'all 0.2s ease',
                            transform: hoveredItem === `cantera-${group.id}` ? 'translateX(3px)' : 'translateX(0)',
                            '&:hover': { color: '#FFCC80' },
                          }}
                          onClick={() => handleCanteraGroupClick(group.id)}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: group.code_couleur || '#ffffff',
                              mr: 1.5,
                              opacity: 0.8,
                            }}
                          />
                          <ListItemText primary={group.libelle} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Cas normal (items uniques sans sous-menu)
            return (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={() => handleMenuItemClick(item)}
                  onMouseEnter={() => setHoveredItem(item)}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                    margin: '4px 12px',
                    borderRadius: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor:
                      activeMenuItem === item ? 'rgba(255, 255, 255, 0.15)' :
                      hoveredItem === item ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    transform: hoveredItem === item ? 'translateX(5px)' : 'translateX(0)',
                    '&:hover': {
                      color: '#FFCC80',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    cursor: 'pointer',
                  }}
                >
                  {item === t('layout.menu.main') && (
                    <DashboardIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.players') && (
                    <GroupsIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {(item === t('layout.menu.rankings') || item === t('layout.menu.lists')) && (
                    <ViewKanbanIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.competitions') && (
                    <MilitaryTech
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.tracking') && (
                    <VisibilityIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.reports') && (
                    <AssignmentIndIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.intel') && (
                    <SecurityIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.news') && (
                    <ArticleIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.bestOfDay') && (
                    <EmojiEventsIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.agencies') && (
                    <BusinessCenterIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.settingsMenu.teamAssignment') && (
                    <SportsSoccerIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}
                  {item === t('layout.menu.cantera') && (
                    <GroupWorkIcon
                      sx={{
                        color: activeMenuItem === item ? '#FFCC80' : '#ffffff',
                        mr: 2,
                      }}
                    />
                  )}

                  <ListItemText primary={item} />
                </ListItem>
              </React.Fragment>
            );
          })}

        </List>
      </Drawer>

      {/* CONTENU PRINCIPAL   */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          backgroundColor: '#f8f9fb',
          minHeight: '100vh',
          minWidth: 0,
          transition: 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          marginLeft: isLargeScreen && menuOpen ? '200px' : '0px',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Footer confidentiel fixe en bas */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          py: 0.3,
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 1200,
        }}
      >
        <Typography sx={{ color: 'rgba(0,0,0,0.2)', fontSize: '0.55rem', letterSpacing: '0.05em' }}>
          CONFIDENTIAL – {t('legal.propertyOf', { club: clubName })}
        </Typography>
      </Box>

      {/* Popover pour afficher la liste complète des matchs */}
      <Popover
        open={Boolean(matchesListAnchor)}
        anchorEl={matchesListAnchor}
        onClose={handleCloseMatchesList}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            maxWidth: '600px',
            minWidth: '400px',
            maxHeight: '70vh',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: primaryColor, mb: 2, textAlign: 'center' }}>
            {t('layout.matchesOfDay')} ({matches.length})
          </Typography>
          <List sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {matches.map((match, index) => (
              <ListItem
                key={index}
                button
                onClick={(event) => {
                  handleCloseMatchesList();
                  handleMatchClick(event, match, index);
                }}
                sx={{
                  mb: 1,
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#FFF3E0',
                    transform: 'translateX(5px)',
                    borderColor: primaryColor
                  }
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {/* Logo équipe 1 */}
                      {match.logo1 && match.logo1.startsWith('http') ? (
                        <img src={match.logo1} alt={match.team1} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                      ) : (
                        <span style={{ fontSize: '20px' }}>{match.logo1}</span>
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {match.team1}
                      </Typography>
                    </Box>
                    {match.chip > 0 && (
                      <Chip
                        label={match.chip}
                        size="small"
                        sx={{
                          backgroundColor: primaryColor,
                          color: 'white',
                          fontWeight: 'bold',
                          height: '20px',
                          minWidth: '20px',
                          '& .MuiChip-label': {
                            px: 0.5,
                          }
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {/* Logo équipe 2 */}
                    {match.logo2 && match.logo2.startsWith('http') ? (
                      <img src={match.logo2} alt={match.team2} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: '20px' }}>{match.logo2}</span>
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {match.team2}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    {match.league} - {match.round}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      {/* Popover pour afficher les joueurs du match */}
      <Popover
        open={Boolean(matchPopoverAnchor)}
        anchorEl={matchPopoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            maxWidth: '700px',
            minWidth: '500px',
            maxHeight: '80vh',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            overflow: 'auto'
          }
        }}
      >
        {selectedMatch && (
          <Box sx={{ p: 2 }}>
            {/* Header sans navigation (maintenant dans le layout) */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              pb: 2,
              borderBottom: '2px solid #f0f0f0'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
                  {/* Logo équipe 1 (toujours l'équipe à domicile) */}
                  {selectedMatch.logo1 && selectedMatch.logo1.startsWith('http') ? (
                    <img src={selectedMatch.logo1} alt={selectedMatch.team1} style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '24px' }}>{selectedMatch.logo1}</span>
                  )}

                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: primaryColor }}>
                    {selectedMatch.team1} vs {selectedMatch.team2}
                  </Typography>

                  {/* Logo équipe 2 (toujours l'équipe à l'extérieur) */}
                  {selectedMatch.logo2 && selectedMatch.logo2.startsWith('http') ? (
                    <img src={selectedMatch.logo2} alt={selectedMatch.team2} style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '24px' }}>{selectedMatch.logo2}</span>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {selectedMatch.league} - {selectedMatch.round}
                </Typography>
              </Box>
            </Box>

            {/* Contenu: Liste des joueurs */}
            {loadingPlayers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: primaryColor }} />
              </Box>
            ) : matchPlayers ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Toujours afficher l'équipe à domicile (team1) en premier */}
                {[selectedMatch.team1, selectedMatch.team2].map((teamName) => {
                  const players = matchPlayers[teamName] || [];
                  return (
                    <Box key={teamName} sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'bold',
                          mb: 1,
                          p: 1,
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}
                      >
                        {teamName}
                      </Typography>

                      {players.length > 0 ? (
                        <List dense sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {players.map((player) => (
                            <ListItem
                              key={player.id}
                              button
                              onClick={() => {
                                handleClosePopover();
                                navigate(`/players/${player.id}`);
                              }}
                              sx={{
                                mb: 0.5,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#FFF3E0',
                                  transform: 'translateX(5px)'
                                }
                              }}
                            >
                              <Avatar
                                src={getPlayerAvatarUrl(player.id)}
                                alt={player.nom}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  mr: 1.5,
                                  border: `2px solid ${primaryColor}`
                                }}
                              />
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {player.nom}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    {player.poste} • {player.taille}cm • {player.annee_naissance}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ textAlign: 'center', color: '#999', p: 2 }}>
                          {t('layout.noPlayersRegistered')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ textAlign: 'center', color: '#999', p: 3 }}>
                {t('layout.couldNotLoadPlayers')}
              </Typography>
            )}

            {/* Footer avec le total */}
            {matchPlayers && (
              <Box sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid #f0f0f0',
                textAlign: 'center'
              }}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('layout.total')}: {Object.values(matchPlayers).reduce((sum, players) => sum + players.length, 0)} {t('layout.totalPlayers')}
                </Typography>
              </Box>
            )}

            {/* Bouton Scouting Report */}
            <Box sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid #f0f0f0',
              textAlign: 'center'
            }}>
              <Button
                variant="contained"
                startIcon={loadingScoutingReport ? <CircularProgress size={18} color="inherit" /> : <AssessmentIcon />}
                onClick={handleScoutingReport}
                disabled={loadingScoutingReport}
                sx={{
                  backgroundColor: '#1a1a2e',
                  color: '#F97316',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#16213e',
                  },
                  '&:disabled': {
                    backgroundColor: '#1a1a2e',
                    color: '#F97316',
                    opacity: 0.7,
                  }
                }}
              >
                {loadingScoutingReport ? t('layout.scoutingReportLoading') : t('layout.scoutingReport')}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
}

export default Layout;